import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import * as Tabs from '@radix-ui/react-tabs';
import {
  CSSProperties,
  ComponentPropsWithoutRef,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import DndContext from '../dragAndDrop/DndContext';

import useOptimisticReorder from '../sortableList/useOptimisticReorder';

interface BaseItem {
  id: string;
}

type SortableTabsProps<T extends BaseItem> = {
  value: string | undefined;
  onValueChange: (value: string) => void;
  items: T[];
  // Return `false` to reject the drop (e.g. failed validation); the rendered
  // order will snap back to `items`. Anything else (incl. void) commits.
  onReorder: (
    activeItem: T,
    destinationIndex: number,
    items: T[]
  ) => boolean | void;
  isDisabled?: boolean;
  lockAxis?: boolean;
  hasKeyboardSensor?: boolean;
  activationMode?: 'automatic' | 'manual';
  className?: string;
  // Render-prop children receive the items in their displayed order — map
  // triggers and content from this list (not the `items` prop) so the
  // rendered order matches the drop while the optimistic cache update
  // propagates.
  children: (items: T[]) => ReactNode;
};

const SortableTabs = <T extends BaseItem>(props: SortableTabsProps<T>) => {
  const {
    value,
    onValueChange,
    items,
    onReorder,
    isDisabled,
    lockAxis = true,
    hasKeyboardSensor,
    activationMode = 'automatic',
    className,
    children,
  } = props;

  const { displayItems, commit } = useOptimisticReorder(items);

  return (
    <Tabs.Root
      value={value}
      onValueChange={onValueChange}
      activationMode={activationMode}
      orientation="horizontal"
      className={className}
    >
      <SelectedValueContext.Provider value={value}>
        <DndContext
          sortable
          hasKeyboardSensor={hasKeyboardSensor}
          modifiers={lockAxis ? [restrictToHorizontalAxis] : undefined}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            const activeIndex = displayItems.findIndex(
              ({ id }) => id === active.id
            );
            const overIndex = displayItems.findIndex(
              ({ id }) => id === over.id
            );
            const newItems = arrayMove(displayItems, activeIndex, overIndex);
            const result = onReorder(
              displayItems[activeIndex],
              overIndex,
              newItems
            );
            if (result !== false) commit(newItems);
          }}
        >
          <SortableContext
            items={displayItems}
            strategy={horizontalListSortingStrategy}
            disabled={isDisabled}
          >
            {children(displayItems)}
          </SortableContext>
        </DndContext>
      </SelectedValueContext.Provider>
    </Tabs.Root>
  );
};

type SortableTabsListProps = ComponentPropsWithoutRef<typeof Tabs.List>;

const SortableTabsList: FC<SortableTabsListProps> = (props) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedValue = useContext(SelectedValueContext);

  useEffect(() => {
    if (!selectedValue) return;
    const activeTrigger = listRef.current?.querySelector<HTMLElement>(
      '[role="tab"][data-state="active"]'
    );
    activeTrigger?.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });
  }, [selectedValue]);

  return <Tabs.List {...props} ref={listRef} />;
};

const SelectedValueContext = createContext<string | undefined>(undefined);

type SortableItemContextValue = {
  setActivatorNodeRef: (node: HTMLElement | null) => void;
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
  isDragging: boolean;
  isDisabled: boolean;
  isActive: boolean;
};

const SortableItemContext = createContext<SortableItemContextValue | null>(
  null
);

type SortableTabsItemProps = {
  value: string;
  isDisabled?: boolean;
  className?: string;
  children: ReactNode;
};

// Wraps a sortable tab so the drag handle and tab trigger can sit as
// siblings rather than nested buttons. The wrapping element receives the
// dnd-kit `setNodeRef` and applies the drag transform, while the handle
// and trigger pick up their behaviour from `SortableItemContext`.
const SortableTabsItem: FC<SortableTabsItemProps> = (props) => {
  const { value, isDisabled = false, className, children } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: value, disabled: isDisabled });

  const selectedValue = useContext(SelectedValueContext);
  const isActive = selectedValue === value;

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
  };

  const contextValue = useMemo<SortableItemContextValue>(
    () => ({
      setActivatorNodeRef,
      attributes,
      listeners,
      isDragging,
      isDisabled,
      isActive,
    }),
    [
      setActivatorNodeRef,
      attributes,
      listeners,
      isDragging,
      isDisabled,
      isActive,
    ]
  );

  return (
    <SortableItemContext.Provider value={contextValue}>
      <div
        ref={setNodeRef}
        style={style}
        data-dragging={isDragging || undefined}
        data-state={isActive ? 'active' : 'inactive'}
        className={className}
      >
        {children}
      </div>
    </SortableItemContext.Provider>
  );
};

type SortableTabsTriggerProps = {
  value: string;
  className?: string;
  children: ReactNode;
} & Omit<
  ComponentPropsWithoutRef<'button'>,
  'value' | 'type' | 'className' | 'children'
>;

const SortableTabsTrigger: FC<SortableTabsTriggerProps> = (props) => {
  const { value, className, children, ...rest } = props;
  return (
    <Tabs.Trigger value={value} asChild>
      <button type="button" className={className} {...rest}>
        {children}
      </button>
    </Tabs.Trigger>
  );
};

type SortableTabsHandleProps = {
  label: string;
  className?: string;
};

const SortableTabsHandle: FC<SortableTabsHandleProps> = (props) => {
  const { label, className } = props;
  const ctx = useContext(SortableItemContext);
  if (!ctx) {
    throw new Error(
      'SortableTabsHandle must be rendered inside SortableTabsItem'
    );
  }
  const {
    setActivatorNodeRef,
    attributes,
    listeners,
    isDragging,
    isDisabled,
    isActive,
  } = ctx;

  // Only the active tab's handle joins the roving tab order. Tab → active
  // tab trigger (Radix handles roving for the tablist) → active tab's
  // handle. Inactive handles stay reachable via mouse only.
  return (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      aria-label={`Reorder: ${label}`}
      tabIndex={isActive ? 0 : -1}
      className={clstx(
        'inline-flex items-center justify-center cursor-grab touch-none bg-transparent p-0',
        isDragging && 'cursor-grabbing',
        isDisabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <UntitledIcon name="draggable" className="size-4" />
    </button>
  );
};

type SortableTabsContentProps = ComponentPropsWithoutRef<typeof Tabs.Content>;

const SortableTabsContent: FC<SortableTabsContentProps> = (props) => (
  <Tabs.Content {...props} />
);

/**
 * Non-sortable trigger / content pair for tabs that should stay pinned next
 * to a `SortableTabsList`. They live inside the same `SortableTabs.Root` so
 * the active-value state is shared with the sortable triggers.
 */
const PinnedTabsTrigger = Tabs.Trigger;
const PinnedTabsContent = Tabs.Content;

export {
  PinnedTabsContent,
  PinnedTabsTrigger,
  SortableTabs,
  SortableTabsContent,
  SortableTabsHandle,
  SortableTabsItem,
  SortableTabsList,
  SortableTabsTrigger,
};
